import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import { CaretDown, Check } from "@phosphor-icons/react";

/**
 * Language switcher with flag emojis. Fully accessible (ARIA + keyboard nav).
 * Stores preference in localStorage via i18next detector.
 *
 * Props:
 *   variant: "header" | "footer" | "mobile"
 */
const LANGS = [
  { code: "en", flag: "🇺🇸", labelKey: "lang.en", short: "EN" },
  { code: "es", flag: "🇪🇸", labelKey: "lang.es", short: "ES" },
  { code: "pt", flag: "🇧🇷", labelKey: "lang.pt", short: "PT" },
];

export const LangSwitcher = ({ variant = "header" }) => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = LANGS.find((l) => l.code === i18n.language) || LANGS[0];

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const changeLang = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
    // Update <html lang="..."> for accessibility & SEO
    document.documentElement.lang = code;
  };

  const isMobile = variant === "mobile";

  return (
    <div ref={ref} className={`relative ${isMobile ? "w-full" : ""}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("lang.select_language")}
        data-testid={`lang-switcher-${variant}`}
        className={
          isMobile
            ? "flex items-center justify-between w-full gap-2 px-3 py-2 border border-as-border text-white hover:border-as-green transition-colors"
            : "flex items-center gap-1.5 px-2 py-1.5 text-white hover:text-as-green transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-as-green rounded-sm"
        }
      >
        <span aria-hidden="true" className="text-lg leading-none">{current.flag}</span>
        <span className="as-label text-[11px] tracking-widest">{current.short}</span>
        <CaretDown size={12} weight="bold" aria-hidden="true" className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={t("lang.select_language")}
          className={
            isMobile
              ? "mt-2 w-full bg-as-black border border-as-border"
              : "absolute right-0 mt-2 min-w-[180px] bg-as-black border border-as-border shadow-xl z-50"
          }
          data-testid="lang-switcher-menu"
        >
          {LANGS.map((l) => {
            const active = l.code === current.code;
            return (
              <li key={l.code} role="none">
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => changeLang(l.code)}
                  data-testid={`lang-option-${l.code}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-as-surface transition-colors ${
                    active ? "text-as-green" : "text-white"
                  }`}
                >
                  <span aria-hidden="true" className="text-xl leading-none">{l.flag}</span>
                  <span className="flex-1 text-sm">{t(l.labelKey)}</span>
                  {active && <Check size={14} weight="bold" aria-hidden="true" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LangSwitcher;
